import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { BRAND } from "../data/staticData";
import Reveal from "../components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll respond within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="container-fld py-20">
        <div className="overline mb-4">// Contact</div>
        <h1 className="font-tactical text-5xl md:text-7xl font-bold uppercase text-white tracking-tight leading-none">
          Get in <span className="text-[#7CFF00]">Comms.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-zinc-400 text-lg leading-relaxed">
          Real veterans on the other end of every message. We respond within 24
          hours, every time.
        </p>
      </section>

      <section className="container-fld pb-24 grid md:grid-cols-12 gap-8">
        <Reveal className="md:col-span-5 space-y-3">
          <ContactCard icon={Phone} label="Phone" value={BRAND.phone} href={`tel:${BRAND.phoneRaw}`} testid="contact-phone" />
          <ContactCard icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testid="contact-email-link" />
          <ContactCard icon={MapPin} label="Location" value={BRAND.location} testid="contact-location" />

          <div className="tactical-card p-6 mt-4">
            <div className="overline mb-3">// Hours</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li className="flex justify-between">
                <span>Mon — Fri</span>
                <span className="text-white">9:00 — 18:00</span>
              </li>
              <li className="flex justify-between">
                <span>Saturday</span>
                <span className="text-white">8:00 — 16:00</span>
              </li>
              <li className="flex justify-between">
                <span>Sunday</span>
                <span className="text-zinc-500">By appointment</span>
              </li>
            </ul>
          </div>
        </Reveal>

        <Reveal delay={120} className="md:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="tactical-card p-6 md:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline block mb-2">Your Name</label>
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none"
                />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input
                  required
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Subject</label>
              <input
                data-testid="contact-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none"
              />
            </div>
            <div>
              <label className="overline block mb-2">Message</label>
              <textarea
                required
                rows={6}
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/15 px-4 py-3 text-white focus:border-[#7CFF00] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="btn-primary disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {busy ? "Sending..." : "Send Message"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, href, testid }) {
  const Body = (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 border border-[#7CFF00] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#7CFF00]" />
      </div>
      <div>
        <div className="overline text-[10px]">{label}</div>
        <div className="font-tactical uppercase text-white tracking-wider mt-1">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      data-testid={testid}
      className="tactical-card p-5 block hover:border-[#7CFF00]/60 transition-colors"
    >
      {Body}
    </a>
  ) : (
    <div data-testid={testid} className="tactical-card p-5">
      {Body}
    </div>
  );
}
